import ENDPOINTS from "@/plugins/axios/endpoints";
import Vue from "vue";
import moment from "moment";

// helper: format seconds into MM:SS
function formatTime(seconds) {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return (
    (minutes < 10 ? "0" + minutes : minutes) +
    ":" +
    (secs < 10 ? "0" + secs : secs)
  );
}

// interval refs live outside Vuex state so Vue.set / spreads can't clobber them
const _groupTimerIntervals = {};
let _globalTimerInterval = null;
let _ayceVisibilityHandlerAttached = false;

// Timers are derived from wall-clock time, not from setInterval ticks: browsers freeze
// background intervals, which would otherwise stall the countdown and never unlock the group.
function _ayceTableUuid() {
  return (window.store && window.store.getters["Table/uuid"]) || "default";
}
function _groupStorageKey() {
  return `ayce_group_timers_${_ayceTableUuid()}`;
}
function _globalStorageKey() {
  return `ayce_global_timer_${_ayceTableUuid()}`;
}
function _readGroupStore() {
  try {
    return JSON.parse(localStorage.getItem(_groupStorageKey()) || "{}");
  } catch (e) {
    return {};
  }
}
function _writeGroupStore(obj) {
  localStorage.setItem(_groupStorageKey(), JSON.stringify(obj));
}
function _remainingFrom(startedAt, totalSeconds) {
  return totalSeconds - Math.floor((Date.now() - startedAt) / 1000);
}
// resync on focus so a group unlocks the instant the user returns to a backgrounded tab
function _ensureVisibilityResync() {
  if (_ayceVisibilityHandlerAttached) return;
  if (
    typeof window === "undefined" ||
    !window.store ||
    typeof document === "undefined"
  )
    return;
  const resync = () => window.store.dispatch("Config/resyncAyceTimers");
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") resync();
  });
  window.addEventListener("focus", resync);
  _ayceVisibilityHandlerAttached = true;
}

const Color = require("color");

// POS sends "0" for unset, "Array" for broken
function parseColor(value) {
  if (value === null || value === undefined) return null;
  const raw = String(value).trim();
  if (raw === "" || raw === "0" || raw === "Array") return null;
  try {
    return Color(raw);
  } catch (e) {
    return null;
  }
}

export default {
  namespaced: true,
  state: {
    themeColors: null,
    textColors: null,
    images: null,
    imageServerBaseUrl: "",
    imageServerImagePath: "",
    fontFamily: null,
    hide_product_details: false,
    messages: null,
    is_paypal_configured: false,
    Privacy: "",
    allergens: "",
    global_product_info: null,
    openingTimes: [],
    status: true, // service status
    payments: [], //list of activated payments
    paymentTranslations: {}, //list of payment translations
    show_allergens: false,
    show_default_product_info: false,
    show_product_description: false,
    hideProductDescriptionInMenu: false,
    default_product_description: "",
    hide_waiter_button: false,
    can_order_as_group: false,
    tb_redirectAfterOrder: "0",
    tb_redirectToUrlAfterOrder: "",
    productCardStyle: null,
    enabledLanguages: null,
    default_language: "de",
    geoLocale: "de",
    appLang: null,
    selectPaymentMsg: "",
    isReadModeOny: false,
    queryReadOnly: false,
    tb_enableSMSSending: false,
    isTipDialogEnabled: false,
    tipPercentages: {},
    tipDialogText: "",
    enableAllYouCanEatMode: false,
    enableAllYouCanEatModeSettings: {
      disablePartyName: true,
      skipPaymentScreen: true,
      timeBetweenOrders: null,
      enabledItemGroupIds: [],
      freeText: {}
    },
    promoCodesActive: false,
    timeForNextOrder: "00:00", //minutes
    timeForNextOrderInSeconds: 0, // seconds
    timeForNextOrderTimer: null,
    // per-group timers: { [groupId]: { secondsRemaining: N, intervalRef: <id> } }
    ayceGroupTimers: {}
  },
  getters: {
    promoCodesActive: state => state.promoCodesActive,
    tipDialogText: state => state.tipDialogText,
    queryReadOnly: state => state.queryReadOnly,
    tipPercentages: state => state.tipPercentages,
    isTipDialogEnabled: state => state.isTipDialogEnabled,
    hideProductDescriptionInMenu: state => state.hideProductDescriptionInMenu,
    appLang: state => state.appLang,
    isReadonly: state => state.isReadModeOny || state.queryReadOnly,
    tb_enableSMSSending: state => state.tb_enableSMSSending,
    selectPaymentMsg: state => state.selectPaymentMsg,
    defaultLanguage: state => state.default_language || "de",
    languages: state => {
      if (
        Array.isArray(state.enabledLanguages) &&
        state.enabledLanguages.length > 0
      )
        return [...state.enabledLanguages];
      else return ["de"];
    },
    productCardStyle: state => {
      let style = {};
      if (state.productCardStyle) {
        if (
          Object.prototype.hasOwnProperty.call(
            state.productCardStyle,
            "border_size"
          )
        )
          style.border = `${state.productCardStyle.border_size}px solid !important`;
        if (
          Object.prototype.hasOwnProperty.call(
            state.productCardStyle,
            "border_color"
          )
        )
          style[
            "border-color"
          ] = `${state.productCardStyle.border_color} !important`;
        if (
          Object.prototype.hasOwnProperty.call(
            state.productCardStyle,
            "border_radius"
          )
        )
          style[
            "border-radius"
          ] = `${state.productCardStyle.border_radius}px !important`;
      }
      return { ...style };
    },
    can_order_as_group: state => state.can_order_as_group,
    tb_redirectAfterOrder: state => state.tb_redirectAfterOrder,
    tb_redirectToUrlAfterOrder: state => state.tb_redirectToUrlAfterOrder,
    show_allergens: state => state.show_allergens,
    show_default_product_info: state => state.show_default_product_info,
    show_product_description: state => state.show_product_description,
    default_product_description: state => state.default_product_description,
    hide_waiter_button: state => state.hide_waiter_button,
    payments: state => state.payments || [],
    paymentTranslations: state => state.paymentTranslations || {},
    openingTimes: state => state.openingTimes,
    status: state => state.status,
    is_available: (state, getters) => {
      return state.status && getters.isStillOpen;
    },
    isStillOpen: (state, getters) => {
      let today = new Date().getDay() - 1 === -1 ? 6 : new Date().getDay() - 1;
      let now = moment();
      //  console.log('before', today)
      if (getters.openingTimes && getters.openingTimes.length > 0) {
        // console.log('in goood',)
        if (getters.openingTimes.length > today) {
          //console.log('still not bad',)
          if (getters.openingTimes[today].open) {
            if (getters.openingTimes[today].open24h) {
              return true;
            }

            //console.log('but',)
            let start1 =
              getters.openingTimes[today]["shift1"].start !== "00:00:00.000000"
                ? getters.openingTimes[today]["shift1"].start.substring(0, 5)
                : null;
            let end1 =
              getters.openingTimes[today]["shift1"].end !== "00:00:00.000000"
                ? getters.openingTimes[today]["shift1"].end.substring(0, 5)
                : null;
            let start2 =
              getters.openingTimes[today]["shift2"].start !== "00:00:00.000000"
                ? getters.openingTimes[today]["shift2"].start.substring(0, 5)
                : null;
            let end2 =
              getters.openingTimes[today]["shift2"].end !== "00:00:00.000000"
                ? getters.openingTimes[today]["shift2"].end.substring(0, 5)
                : null;

            if (getters.openingTimes[today]["shift1"].open && start1 && end1) {
              if (
                now.isBetween(
                  moment(start1, "HH:mm"),
                  moment(end1, "HH:mm")
                ) /* || now.isBefore(moment(end1, "HH:mm"))*/
              ) {
                return true;
              }
            }
            //console.log('shift 2 ddata: ', start2, end2)
            if (getters.openingTimes[today]["shift2"].open && start2 && end2) {
              //  console.log("shift 2 open ", now.isBetween(moment(start2, "HH:mm"), moment(end2, "HH:mm")) || now.isBefore(moment(end2, "HH:mm")), moment(start2, "HH:mm"), moment(end2, "HH:mm"))
              if (
                now.isBetween(
                  moment(start2, "HH:mm"),
                  moment(end2, "HH:mm")
                ) /* || now.isBefore(moment(end2, "HH:mm"))*/
              ) {
                return true;
              }
            }
          }
        }
      }
      return false;
    },
    privacy: state => state.Privacy,
    allergens: state => state.allergens,
    isPaypalConfigured: state => parseInt(state.is_paypal_configured),
    globalProductInfo: state => state.global_product_info,
    ThemeColors: state => state.themeColors || {},
    TextColors: state => state.textColors || {},
    Images: state => state.images,
    imageServerBaseUrl: state => state.imageServerBaseUrl,
    imageServerImagePath: state => state.imageServerImagePath,
    FontFamily: state => state.fontFamily,
    Messages: state => state.messages,
    hideProductDetails: state => state.hide_product_details,
    geoLocale: state => state.geoLocale || "de",
    isConfigured: state => {
      if (state.themeColors) {
        if (!state.themeColors.primary) return false;
        if (!state.themeColors.secondary) return false;
        if (!state.themeColors.background) return false;
        if (!state.themeColors.accent) return false;
        if (!state.themeColors.product_card) return false;
      } else return false;

      if (state.textColors) {
        if (!state.textColors.product_name) return false;
        if (!state.textColors.product_description) return false;
      } else return false;

      if (state.messages) {
        if (!state.messages.start_page) return false;
        if (!state.messages.ask_for_name_page) return false;
      } else return false;

      return !!state.fontFamily;
    },
    enableAllYouCanEatMode: state => state.enableAllYouCanEatMode,
    enableAllYouCanEatModeSettings: state =>
      state.enableAllYouCanEatModeSettings,
    timeForNextOrder: state => state.timeForNextOrder,
    // true when AYCE is on AND enabledItemGroupIds has entries AND timeBetweenOrders > 0
    isAycePerGroupMode: state => {
      return (
        state.enableAllYouCanEatMode === true &&
        Array.isArray(
          state.enableAllYouCanEatModeSettings?.enabledItemGroupIds
        ) &&
        state.enableAllYouCanEatModeSettings.enabledItemGroupIds.length > 0 &&
        Number(state.enableAllYouCanEatModeSettings?.timeBetweenOrders) > 0
      );
    },
    // returns array of groupIds currently under cooldown
    ayceLockedGroupIds: state => {
      return Object.keys(state.ayceGroupTimers)
        .filter(id => state.ayceGroupTimers[id].secondsRemaining > 0)
        .map(id => Number(id));
    },
    // returns formatted "MM:SS" for a specific groupId
    ayceGroupTimerFormatted: state => groupId => {
      const entry = state.ayceGroupTimers[groupId];
      if (!entry || entry.secondsRemaining <= 0) return "00:00";
      return formatTime(entry.secondsRemaining);
    }
  },
  mutations: {
    PROMO_CODES_ACTIVE(state, payload) {
      state.promoCodesActive = !!payload;
    },
    IMAGE_SERVER(state, payload) {
      state.imageServerBaseUrl = payload?.baseUrl || "";
      state.imageServerImagePath = payload?.imagePath || "";
    },
    setQueryReadOnly(state, payload) {
      state.queryReadOnly = payload;
    },
    setGeoLocale(state, payload) {
      state.geoLocale = payload;
    },
    setHideProductDescriptionInMenu(state, payload) {
      state.hideProductDescriptionInMenu = payload;
    },
    SET_TIP_DIALOG_TEXT(state, payload) {
      state.tipDialogText = payload;
    },
    SET_TIP_PERCENTAGES(state, payload) {
      let clone = {};
      try {
        clone = JSON.parse(JSON.stringify(payload));
      } catch (e) {
        /* ignore */
      }
      state.tipPercentages = clone ? clone : {};
    },
    SET_TIP_DIALOG(state, payload) {
      state.isTipDialogEnabled = payload === true;
    },
    SET_APP_LANGUAGE(state, payload) {
      state.appLang = payload;
    },
    SET_SMS_SENDING(state, payload) {
      state.tb_enableSMSSending = payload === true;
    },
    SET_READ_MODE(state, payload) {
      state.isReadModeOny = payload === true;
    },
    SET_SELECT_PAYMENT_MSG(state, payload) {
      state.selectPaymentMsg = payload;
    },
    setDefaultLanguage(state, payload) {
      if (typeof payload === "string" && payload?.length > 1)
        state.default_language = payload?.toLowerCase();
      else state.default_language = "de";
    },
    LANGUAGES(state, payload) {
      if (Array.isArray(payload))
        state.enabledLanguages = payload.map(lang => lang.toLowerCase());
    },
    PRODUCT_CARD_STYLE(state, payload) {
      if (typeof payload === "object") state.productCardStyle = { ...payload };
    },
    CAN_ORDER_AS_GROUP(state, payload) {
      state.can_order_as_group = payload;
    },
    setRedirectAfterOrder(state, payload) {
      state.tb_redirectAfterOrder = payload;
    },
    setRedirectToUrlAfterOrder(state, payload) {
      state.tb_redirectToUrlAfterOrder = payload;
    },
    HIDE_WAITER_BUTTON(state, payload) {
      state.hide_waiter_button = payload;
    },
    ALLERGENS_VISIBILTY(state, payload) {
      state.show_allergens = payload;
    },
    DEFAULT_PRODUCT_INFO_VISIBILTY(state, payload) {
      state.show_default_product_info = payload;
    },
    PRODUCT_DESCRIPTION_VISIBILTY(state, payload) {
      state.show_product_description = payload;
    },
    DEFAULT_PRODUCT_DESCRIPTION(state, payload) {
      state.default_product_description = payload;
    },
    PAYMENTS(state, payload) {
      if (!Array.isArray(payload)) return;
      state.payments = payload;
    },
    CLEARPAYMENTTRANSLATIONS(state) {
      state.paymentTranslations = {};
    },
    PAYMENTTRANSLATIONS(state, payload) {
      Vue.set(state.paymentTranslations, payload.name, payload.value);
    },
    OPENING_TIMES(state, payload) {
      if (Array.isArray(payload)) state.openingTimes = payload;
      else state.openingTimes = [];
    },
    STATUS(state, payload) {
      state.status = payload;
    },
    THEME_COLORS(state, payload) {
      if (typeof payload !== "object") {
        state.themeColors = null;
        return;
      }

      state.themeColors = { ...payload };

      //update vuetify colors
      const primary = parseColor(payload.primary);
      window.vuetify.theme.themes.light.primary.base = primary
        ? primary.hex()
        : "#F17800";

      //secondary
      const secondary = parseColor(payload.secondary);
      window.vuetify.theme.themes.light.secondary = secondary
        ? secondary.hex()
        : "#F4E9E3";

      //bg
      const background = parseColor(payload.background);
      window.vuetify.theme.themes.light.bg = background
        ? background.hex()
        : "#F8F4F1";

      //accent
      const accent = parseColor(payload.accent);
      window.vuetify.theme.themes.light.accent = accent
        ? accent.hex()
        : "#546BBE";

      //tertiary
      const tertiary = parseColor(payload.tertiary);
      if (tertiary) {
        window.vuetify.theme.themes.light.tertiary.base = tertiary.hex();
        window.vuetify.theme.themes.light.tertiary.lighten3 = tertiary
          .lighten(0.06)
          .hex();
      }

      //product card
      const productCard = parseColor(payload.product_card);
      if (productCard)
        window.vuetify.theme.themes.light.product_card = productCard.hex();
    },
    TEXT_COLORS(state, payload) {
      if (typeof payload !== "object") {
        state.textColors = null;
        return;
      }

      state.textColors = { ...payload };

      const productName = parseColor(payload.product_name);
      if (productName)
        window.vuetify.theme.themes.light.product_name = productName.hex();

      const productDescription = parseColor(payload.product_description);
      if (productDescription)
        window.vuetify.theme.themes.light.product_description = productDescription.hex();

      const productPrice =
        parseColor(payload.product_price) ||
        parseColor(window.vuetify.theme.themes.light.primary.base);
      if (productPrice)
        window.vuetify.theme.themes.light.product_price = productPrice.hex();
    },
    IMAGES(state, payload) {
      if (typeof payload !== "object") {
        state.images = null;
        return;
      }

      state.images = { ...payload };
    },
    HIDE_PRODUCT_DETAILS(state, payload) {
      state.hide_product_details = payload;
    },
    FONT_FAMILY(state, payload) {
      state.fontFamily = payload;
    },
    MESSAGES(state, payload) {
      if (typeof payload !== "object") {
        state.messages = null;
        return;
      }
      state.messages = { ...payload };
      state.global_product_info = payload.global_product_info;
    },
    IS_PAYAPL_CONFIGURED(state, payload) {
      state.is_paypal_configured = payload;
    },
    PRIVACY(state, payload) {
      state.Privacy = payload;
    },
    ALLERGENS(state, payload) {
      state.allergens = payload;
    },
    setEnableAllYouCanEatMode(state, payload) {
      state.enableAllYouCanEatMode = payload;
    },
    setEnableAllYouCanEatModeSettings(state, payload) {
      state.enableAllYouCanEatModeSettings = payload;
    },
    // No payload = fresh start after an order. Payload = resume with the original startedAt.
    setTimeForNextOrder(state, payload = {}) {
      if (state.enableAllYouCanEatMode !== true) return;

      const totalSeconds =
        payload.totalSeconds != null
          ? Number(payload.totalSeconds)
          : Number(state.enableAllYouCanEatModeSettings?.timeBetweenOrders) *
              60 || 0;

      if (_globalTimerInterval) {
        clearInterval(_globalTimerInterval);
        _globalTimerInterval = null;
      }

      if (!totalSeconds || totalSeconds <= 0) {
        state.timeForNextOrderInSeconds = 0;
        state.timeForNextOrder = formatTime(0);
        return;
      }

      const startedAt =
        payload.startedAt != null ? Number(payload.startedAt) : Date.now();
      const persistToStorage = payload.persistToStorage !== false;

      if (persistToStorage) {
        localStorage.setItem(
          _globalStorageKey(),
          JSON.stringify({ startedAt, totalSeconds })
        );
      }

      const remaining = _remainingFrom(startedAt, totalSeconds);
      state.timeForNextOrderInSeconds = Math.max(remaining, 0);
      state.timeForNextOrder = formatTime(Math.max(remaining, 0));

      if (remaining <= 0) {
        localStorage.removeItem(_globalStorageKey());
        return;
      }

      _ensureVisibilityResync();

      _globalTimerInterval = setInterval(() => {
        const rem = _remainingFrom(startedAt, totalSeconds);
        window.store.commit("Config/setGlobalTimerSeconds", Math.max(rem, 0));

        if (rem <= 0) {
          clearInterval(_globalTimerInterval);
          _globalTimerInterval = null;
          localStorage.removeItem(_globalStorageKey());
        }
      }, 1000);
    },

    setGlobalTimerSeconds(state, seconds) {
      state.timeForNextOrderInSeconds = seconds;
      state.timeForNextOrder = formatTime(seconds);
    },

    // New order passes startedAt = now; resume passes the stored startedAt with persistToStorage = false.
    startGroupTimer(
      state,
      { groupId, totalSeconds, startedAt, persistToStorage = true }
    ) {
      const key = String(groupId);

      if (_groupTimerIntervals[key]) {
        clearInterval(_groupTimerIntervals[key]);
        delete _groupTimerIntervals[key];
      }

      if (persistToStorage) {
        const stored = _readGroupStore();
        stored[key] = { startedAt, totalSeconds };
        _writeGroupStore(stored);
      }

      const remaining = _remainingFrom(startedAt, totalSeconds);
      Vue.set(state.ayceGroupTimers, key, {
        secondsRemaining: Math.max(remaining, 0)
      });

      if (remaining <= 0) {
        const stored = _readGroupStore();
        delete stored[key];
        _writeGroupStore(stored);
        return;
      }

      _ensureVisibilityResync();

      const intervalId = setInterval(() => {
        const rem = _remainingFrom(startedAt, totalSeconds);
        window.store.commit("Config/updateGroupTimerSeconds", {
          groupId: key,
          seconds: Math.max(rem, 0)
        });

        if (rem <= 0) {
          clearInterval(intervalId);
          delete _groupTimerIntervals[key];
          const stored = _readGroupStore();
          delete stored[key];
          _writeGroupStore(stored);
        }
      }, 1000);

      _groupTimerIntervals[key] = intervalId;
    },

    updateGroupTimerSeconds(state, { groupId, seconds }) {
      Vue.set(state.ayceGroupTimers, String(groupId), {
        secondsRemaining: seconds
      });
    }

    // setSessionTimeout() {
    //   const session_id = "rTH89QZKkc6jybdqpVijMIWsO9Z5gK1u62Ma68B1";
    //   const session_cookie_name = "3pos_session";
    //   // const cookie_domain = "services.api.testing.3pos.de"; // must match backend
    //   const cookie_path = "/";
    //   const cookie_lifetime = 30; // in minutes

    //   const expiryDate = new Date(Date.now() + cookie_lifetime * 60000);

    //   const cookieStr = `${session_cookie_name}=${session_id}; Path=${cookie_path}; Expires=${expiryDate.toUTCString()}; HttpOnly; SameSite=None; Secure`;

    //   document.cookie = cookieStr;
    // }
  },
  actions: {
    startTimeBetweenOrdersTimer({ commit }) {
      commit("setTimeForNextOrder");
    },

    // Start a cooldown for every ordered group that's subject to the AYCE timer.
    startGroupTimersForOrder({ state, commit }, { orderedItems }) {
      const enabledGroupIds =
        state.enableAllYouCanEatModeSettings?.enabledItemGroupIds;
      const timeBetweenOrders = Number(
        state.enableAllYouCanEatModeSettings?.timeBetweenOrders
      );

      if (!Array.isArray(enabledGroupIds) || enabledGroupIds.length === 0)
        return;
      if (!timeBetweenOrders || timeBetweenOrders <= 0) return;

      const startedAt = Date.now();
      const totalSeconds = timeBetweenOrders * 60;

      const groupIds = [
        ...new Set(
          (orderedItems || [])
            .map(entry => entry.item?.category_id)
            .filter(
              id =>
                id !== undefined &&
                id !== null &&
                enabledGroupIds.includes(Number(id))
            )
        )
      ];

      groupIds.forEach(groupId => {
        commit("startGroupTimer", {
          groupId: Number(groupId),
          totalSeconds,
          startedAt
        });
      });
    },

    // Resume per-group cooldowns after a refresh, dropping any that have already expired.
    resumeGroupTimersFromStorage({ state, commit }) {
      if (state.enableAllYouCanEatMode !== true) return;

      const timeBetweenOrders = Number(
        state.enableAllYouCanEatModeSettings?.timeBetweenOrders
      );
      if (!timeBetweenOrders || timeBetweenOrders <= 0) return;

      const stored = _readGroupStore();
      let changed = false;

      Object.entries(stored).forEach(([groupId, data]) => {
        if (_groupTimerIntervals[String(groupId)]) return;

        const remaining = _remainingFrom(data.startedAt, data.totalSeconds);
        if (remaining > 0) {
          commit("startGroupTimer", {
            groupId: Number(groupId),
            totalSeconds: data.totalSeconds,
            startedAt: data.startedAt,
            persistToStorage: false
          });
        } else {
          delete stored[groupId];
          changed = true;
        }
      });

      if (changed) _writeGroupStore(stored);
    },

    // Resume the global countdown after a refresh. Only relevant when no groups are configured.
    resumeGlobalTimerFromStorage({ state, commit }) {
      if (state.enableAllYouCanEatMode !== true) return;

      const enabledGroupIds =
        state.enableAllYouCanEatModeSettings?.enabledItemGroupIds;
      if (Array.isArray(enabledGroupIds) && enabledGroupIds.length > 0) return;

      let data;
      try {
        data = JSON.parse(localStorage.getItem(_globalStorageKey()) || "null");
      } catch (e) {
        data = null;
      }
      if (!data || !data.startedAt || !data.totalSeconds) return;

      const remaining = _remainingFrom(data.startedAt, data.totalSeconds);
      if (remaining > 0) {
        commit("setTimeForNextOrder", {
          startedAt: data.startedAt,
          totalSeconds: data.totalSeconds,
          persistToStorage: false
        });
      } else {
        localStorage.removeItem(_globalStorageKey());
      }
    },

    // Recompute every timer on focus so groups unlock instantly when the tab comes back.
    resyncAyceTimers({ state, commit }) {
      if (state.enableAllYouCanEatMode !== true) return;

      try {
        const data = JSON.parse(
          localStorage.getItem(_globalStorageKey()) || "null"
        );
        if (data && data.startedAt && data.totalSeconds) {
          const rem = _remainingFrom(data.startedAt, data.totalSeconds);
          if (rem > 0) {
            commit("setGlobalTimerSeconds", rem);
          } else {
            if (_globalTimerInterval) {
              clearInterval(_globalTimerInterval);
              _globalTimerInterval = null;
            }
            commit("setGlobalTimerSeconds", 0);
            localStorage.removeItem(_globalStorageKey());
          }
        }
      } catch (e) {
        /* ignore malformed storage */
      }

      const stored = _readGroupStore();
      let changed = false;
      Object.entries(stored).forEach(([groupId, data]) => {
        const key = String(groupId);
        const rem = _remainingFrom(data.startedAt, data.totalSeconds);
        if (rem > 0) {
          commit("updateGroupTimerSeconds", { groupId: key, seconds: rem });
        } else {
          if (_groupTimerIntervals[key]) {
            clearInterval(_groupTimerIntervals[key]);
            delete _groupTimerIntervals[key];
          }
          commit("updateGroupTimerSeconds", { groupId: key, seconds: 0 });
          delete stored[groupId];
          changed = true;
        }
      });

      if (changed) _writeGroupStore(stored);
    },

    loadConfig({ commit, dispatch }) {
      return new Promise((resolve, reject) => {
        window.store.commit("Loader/startLoading");
        commit("CLEARPAYMENTTRANSLATIONS");
        window.axios
          .get(ENDPOINTS.SELF_SERVICE.CONFIG.GET)
          .then(res => {
            if (res && res.data) {
              commit("THEME_COLORS", res.data.colors);
              commit("MESSAGES", res.data.messages);
              commit("PRIVACY", res.data.messages.privacy);
              commit("ALLERGENS", res.data.Allergens);
              commit("IMAGES", res.data.images);
              commit("IMAGE_SERVER", {
                baseUrl: res.data.imageServerBaseUrl,
                imagePath: res.data.imageServerImagePath
              });
              commit(
                "HIDE_PRODUCT_DETAILS",
                res.data.text.hide_product_details
              );
              commit("FONT_FAMILY", res.data.text.font_family);
              commit("TEXT_COLORS", res.data.text.colors);
              commit("IS_PAYAPL_CONFIGURED", res.data.isPaypalAvailable);
              commit("OPENING_TIMES", res.data.openingTimes);
              commit("STATUS", res.data.status);
              commit("PROMO_CODES_ACTIVE", res.data.promoCodesActive);
              commit("PAYMENTS", res.data.payments);
              window.store.getters["Config/payments"].forEach(p => {
                let tmp = p.id.replace("_active", "_translations");
                if (Object.prototype.hasOwnProperty.call(res.data, tmp)) {
                  commit("PAYMENTTRANSLATIONS", {
                    name: p.id,
                    value: res.data[tmp]
                  });
                }
              });
              commit("ALLERGENS_VISIBILTY", res.data.show_allergens);
              commit(
                "DEFAULT_PRODUCT_INFO_VISIBILTY",
                res.data.show_default_product_info
              );
              commit(
                "PRODUCT_DESCRIPTION_VISIBILTY",
                res.data.show_product_description
              );
              commit(
                "DEFAULT_PRODUCT_DESCRIPTION",
                res.data.default_product_description
              );

              commit(
                "HIDE_WAITER_BUTTON",
                res.data.hide_waiter_button === true
              );

              commit(
                "CAN_ORDER_AS_GROUP",
                res.data.enable_order_as_group === true
              );

              commit("setRedirectAfterOrder", res.data.tb_redirectAfterOrder);

              commit("setGeoLocale", res.data.geoloc_systemcountry);

              commit(
                "setRedirectToUrlAfterOrder",
                res.data.tb_redirectToUrlAfterOrder
              );

              commit("PRODUCT_CARD_STYLE", res.data.productCard);

              commit("LANGUAGES", res.data.enabled_languages);

              commit("setDefaultLanguage", res.data.default_language);

              commit("SET_SELECT_PAYMENT_MSG", res.data.selectPaymentMsg);

              commit("SET_READ_MODE", res.data.tb_enable_read_only);

              commit("SET_SMS_SENDING", res.data.tb_enableSMSSending);

              commit("SET_TIP_DIALOG", res.data.isTipDialogEnabled);

              commit("SET_TIP_PERCENTAGES", res.data.tipPercentages);

              commit("SET_TIP_DIALOG_TEXT", res.data.dynamicTipText);

              commit(
                "setHideProductDescriptionInMenu",
                res.data.tb_hideProductDescription
              );

              commit(
                "setEnableAllYouCanEatMode",
                res.data.tb_enableAllYouCanEatMode === true
              );

              commit(
                "setEnableAllYouCanEatModeSettings",
                res.data.tb_enableAllYouCanEatModeSettings
              );

              dispatch("resumeGroupTimersFromStorage");
              dispatch("resumeGlobalTimerFromStorage");

              // commit("setSessionTimeout");
            }
            resolve();
          })
          .catch(err => {
            reject(err);
          })
          .finally(() => {
            window.store.commit("Loader/stopLoading");
          });
      });
    }
  }
};
